import React, { useEffect, useMemo, useState } from "react";
import {  Button,
  Typography,
  Box,
  Paper,
  TextField,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  alpha,
  useTheme,
  IconButton,
  Alert,
  CircularProgress,} from "@mui/material";
import Grid from "@mui/material/Grid";
import {
  Print as PrintIcon,
  Edit as EditIcon,
  HistoryEdu,
  CheckCircle,
  Close as CloseIcon,
  Timer as TimerIcon,
} from "@mui/icons-material";
import { DatePicker, DateTimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";

import { renderProtestV2 } from "../../print-v2";
import protestOptions from "./utils/protestOptions";
import getPrefillValue from "../../utils/getPrefillValue";
import { useVoyageContextV2 } from "../../Contexts/VoyageContextV2";
import { useShipSettings } from "../../Contexts/ShipSettingsContext";
import { useCompanyContext } from "../../Contexts/CompanyContext";
import { useUserContext } from "../../Contexts/UserContext";
import { getShipName } from "../../utils/domainSelectors";
import { getSelectedCargo, getSelectedTrip } from "../../utils/voyageSelection";

const Protests = () => {
  const theme = useTheme();
  const { shipData } = useShipSettings();
  const { companyLogo } = useCompanyContext();
  const { user } = useUserContext();
  const {
    selectedTripId,
    selectedCargoIndex,
    currentVoyage,
    isVoyageReadOnly,
    updateTrip,
    addLog,
  } = useVoyageContextV2();

  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "warning",
  });
  const [isEditMode, setIsEditMode] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProtest, setSelectedProtest] = useState(null);
  const deadweight = formData["Deadweight"];
  const bunkers = formData["Bunkers"];
  const fw = formData["FW"];
  const stores = formData["Stores"];
  const selectedTrip = getSelectedTrip(currentVoyage, selectedTripId);
  const selectedCargo = getSelectedCargo(
    currentVoyage,
    selectedTripId,
    selectedCargoIndex
  );
  const tripId = selectedTrip?.tripId || selectedTripId;
  const tripProtestData = useMemo(
    () => selectedTrip?.protestData || {},
    [selectedTrip]
  );

  useEffect(() => {
    if (currentVoyage) {
      setLoading(false);
    }
  }, [currentVoyage]);

  // Logik för prefill och beräkningar
  useEffect(() => {
    if (selectedProtest) {
      const protestTemplate = protestOptions.find(
        (p) => p.id === selectedProtest.id
      );
      if (protestTemplate) {
        const prefilledData = protestTemplate.fields.reduce((acc, field) => {
          acc[field.label] = getPrefillValue(
            field.label,
            currentVoyage,
            selectedTripId,
            selectedCargoIndex,
            shipData
          );
          return acc;
        }, {});
        const savedData = tripProtestData[selectedProtest.id]?.fields || {};
        setFormData({ ...prefilledData, ...savedData });
      }
    }
  }, [
    tripProtestData,
    selectedProtest,
    selectedTripId,
    selectedCargoIndex,
    currentVoyage,
    shipData,
  ]);

  // Automatisk beräkning av Cargo Capacity
  useEffect(() => {
    if (deadweight !== undefined) {
      const dw = parseFloat(deadweight) || 0;
      const bunkerValue = parseFloat(bunkers) || 0;
      const fwValue = parseFloat(fw) || 0;
      const storesValue = parseFloat(stores) || 0;
      const cargoCapacity = (dw - bunkerValue - fwValue - storesValue).toString();

      setFormData((prev) => ({
        ...prev,
        "Cargo Capacity":
          prev["Cargo Capacity"] === cargoCapacity
            ? prev["Cargo Capacity"]
            : cargoCapacity,
      }));
    }
  }, [deadweight, bunkers, fw, stores]);

  const handleToggleEditMode = async () => {
    if (isVoyageReadOnly) return;

    if (isEditMode && selectedProtest) {
      await persistProtest({
        status: tripProtestData[selectedProtest.id]?.status || "Draft",
        fields: {
          ...formData,
          protest: selectedProtest.id,
          protestTitle: selectedProtest.title,
        },
      });
    }

    setIsEditMode(!isEditMode);
  };

  const persistProtest = async ({ status, printedTime, fields }) => {
    if (!tripId || !selectedProtest || isVoyageReadOnly) return false;

    const nextProtestData = {
      ...tripProtestData,
      [selectedProtest.id]: {
        ...(tripProtestData[selectedProtest.id] || {}),
        protestId: selectedProtest.id,
        protestTitle: selectedProtest.title,
        status,
        fields,
        printedTime:
          printedTime || tripProtestData[selectedProtest.id]?.printedTime || null,
        updatedAt: new Date().toISOString(),
      },
    };

    return updateTrip(
      tripId,
      { protestData: nextProtestData },
      {
        summary:
          status === "Printed"
            ? `Printed protest ${selectedProtest.title}`
            : `Updated protest ${selectedProtest.title}`,
      }
    );
  };

  const handlePrint = async () => {
    if (!selectedProtest || !selectedTrip) return;

    const inputValues = { ...formData };
    inputValues["ship"] = getShipName(shipData) || currentVoyage?.vessel || "";
    inputValues["protest"] = selectedProtest.id;
    inputValues["protestTitle"] = selectedProtest.title;

    // Hantera Shore Stops logik här om den behövs för print-funktionen
    const shoreStops = getShoreStops();
    inputValues["shoreStops"] = shoreStops;
    const printedTime = new Date().toISOString();

    await persistProtest({
      status: "Printed",
      printedTime,
      fields: inputValues,
    });

    await addLog({
      user: user?.username || user?.userName || "Unknown",
      activity: "Print Protest",
      details: selectedProtest.title,
      tripId,
    });

    renderProtestV2({ inputValues, companyLogo });
    setModalOpen(false);
    setIsEditMode(false);
    setSnackbar({
      open: true,
      message: "Protest generated successfully",
      severity: "success",
    });
  };

  const getShoreStops = () => {
    const log =
      selectedTrip?.operationType === "Discharging"
        ? selectedCargo?.dischargingLog
        : selectedCargo?.loadingLog;

    let stops = [];
    if (log) {
      log.forEach((entry, idx) => {
        if (
          entry.remarks.toLowerCase().includes("shore") &&
          entry.remarks.toLowerCase().includes("stop")
        ) {
          const startTime = dayjs(entry.localTime);
          const nextEntry = log[idx + 1];
          const endTime = nextEntry ? dayjs(nextEntry.localTime) : null;

          stops.push({
            index: stops.length + 1,
            startTime: entry.localTime,
            stopTime: nextEntry?.localTime,
            reason: entry.remarks,
            stopDuration: endTime
              ? endTime.diff(startTime, "hour", true).toFixed(1)
              : "N/A",
          });
        }
      });
    }
    return stops;
  };

  const renderField = (field) => {
    // Specialfall: Shore Stops (Read Only i V2)
    if (field.label === "Shore stops") {
      const stops = getShoreStops();
      return (
        <Stack
          spacing={2}
          sx={{
            mt: 1,
            p: 2,
            bgcolor: alpha(theme.palette.action.disabledBackground, 0.05),
            borderRadius: 2,
          }}
        >
          <Typography
            variant="subtitle2"
            display="flex"
            alignItems="center"
            gap={1}
          >
            <TimerIcon fontSize="small" /> Shore Stops Detected from SoF
          </Typography>
          {stops.map((stop, i) => (
            <Box
              key={i}
              sx={{
                p: 1.5,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                bgcolor: "background.paper",
              }}
            >
              <Typography variant="caption" fontWeight="bold">
                Stop #{stop.index}
              </Typography>
              <Grid container spacing={1}>
                <Grid size={6}>
                  <Typography variant="caption" display="block">
                    From: {dayjs(stop.startTime).format("YYYY-MM-DD HH:mm")}
                  </Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="caption" display="block">
                    To:{" "}
                    {stop.stopTime
                      ? dayjs(stop.stopTime).format("YYYY-MM-DD HH:mm")
                      : "Ongoing"}
                  </Typography>
                </Grid>
                <Grid size={12}>
                  <Typography variant="caption" display="block">
                    Reason: {stop.reason}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          ))}
          {stops.length === 0 && (
            <Typography variant="caption">
              No shore stops recorded in log.
            </Typography>
          )}
        </Stack>
      );
    }

    const isFieldDisabled =
      !isEditMode && formData[field.label] && formData[field.label] !== "";

    if (field.type === "date" || field.type === "datetime-local") {
      const Picker = field.type === "date" ? DatePicker : DateTimePicker;
      return (
        <Picker
          label={field.label}
          value={formData[field.label] ? dayjs(formData[field.label]) : null}
          onChange={(v) =>
            setFormData({
              ...formData,
              [field.label]: v
                ? v.format(
                    field.type === "date" ? "YYYY-MM-DD" : "YYYY-MM-DD HH:mm"
                  )
                : "",
            })
          }
          disabled={isFieldDisabled}
          slotProps={{ textField: { fullWidth: true, size: "small" } }}
        />
      );
    }

    const unit =
      field.label === "B/L Figure" || field.label === "Ship Figure"
        ? selectedCargo?.unit
        : null;

    return (
      <TextField
        label={field.label}
        fullWidth
        size="small"
        multiline={field.rows > 1}
        rows={field.rows || 1}
        value={formData[field.label] || ""}
        onChange={(e) =>
          setFormData({ ...formData, [field.label]: e.target.value })
        }
        disabled={isFieldDisabled}
        InputProps={
          unit
            ? {
                endAdornment: (
                  <Typography variant="caption" sx={{ ml: 1 }}>
                    {unit}
                  </Typography>
                ),
              }
            : null
        }
      />
    );
  };

  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
        <CircularProgress />
      </Box>
    );

  return (
    <Box sx={{ maxWidth: 1100, mx: "auto", pb: 5 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="900">
          Sea Protests
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Manage and generate formal letters of protest.
        </Typography>
      </Box>

      {/* Header för listan */}
      <Box sx={{ px: 3, py: 1, display: "flex", opacity: 0.6 }}>
        <Typography variant="caption" sx={{ flex: 1, fontWeight: 800 }}>
          PROTEST TYPE
        </Typography>
        <Typography
          variant="caption"
          sx={{ width: 160, fontWeight: 800, textAlign: "center" }}
        >
          STATUS
        </Typography>
        <Typography
          variant="caption"
          sx={{ width: 180, fontWeight: 800, textAlign: "right" }}
        >
          ACTIONS
        </Typography>
      </Box>

      <Stack spacing={1.5}>
        {protestOptions.map((protest) => {
          const status = tripProtestData?.[protest.id]?.status;
          const isPrinted = status === "Printed";
          const isDraft = status === "Draft";

          return (
            <Paper
              key={protest.id}
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                border: "1px solid",
                borderColor: isPrinted ? "primary.main" : "divider",
                bgcolor: isPrinted
                  ? alpha(theme.palette.primary.main, 0.02)
                  : "background.paper",
                display: "flex",
                alignItems: "center",
                transition: "all 0.2s",
                "&:hover": {
                  borderColor: "primary.main",
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                },
              }}
            >
              <Box
                sx={{ flex: 1, display: "flex", alignItems: "center", gap: 2 }}
              >
                <Box
                  sx={{
                    color: isPrinted ? "primary.main" : "text.disabled",
                    display: "flex",
                  }}
                >
                  <HistoryEdu />
                </Box>
                <Typography variant="body1" fontWeight="700">
                  {protest.title}
                </Typography>
              </Box>

              <Box
                sx={{ width: 160, display: "flex", justifyContent: "center" }}
              >
                {isPrinted ? (
                  <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    sx={{
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      color: "success.main",
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 5,
                    }}
                  >
                    <CheckCircle sx={{ fontSize: 14 }} />
                    <Typography variant="caption" fontWeight="900">
                      PRINTED
                    </Typography>
                  </Stack>
                ) : isDraft ? (
                  <Typography
                    variant="caption"
                    fontWeight="900"
                    color="warning.main"
                  >
                    DRAFT
                  </Typography>
                ) : (
                  <Typography
                    variant="caption"
                    fontWeight="700"
                    color="text.disabled"
                  >
                    PENDING
                  </Typography>
                )}
              </Box>

              <Box sx={{ width: 180, textAlign: "right" }}>
                <Button
                  variant={isPrinted ? "outlined" : "contained"}
                  size="small"
                  fullWidth
                  startIcon={<PrintIcon />}
                  onClick={() => {
                    setSelectedProtest(protest);
                    setIsEditMode(false);
                    setModalOpen(true);
                  }}
                  sx={{ borderRadius: 2, fontWeight: 800, maxWidth: 160 }}
                >
                  {isPrinted ? "RE-PRINT" : "PREPARE"}
                </Button>
              </Box>
            </Paper>
          );
        })}
      </Stack>

      {/* Dialog för ifyllnad */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ p: 3, pb: 2 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box>
              <Typography variant="h6" fontWeight="900">
                Prepare Protest
              </Typography>
              <Typography variant="caption" color="primary" fontWeight="700">
                {selectedProtest?.title}
              </Typography>
            </Box>
            <IconButton onClick={() => setModalOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 3 }}>
          <Stack spacing={3} sx={{ py: 1 }}>
            {selectedProtest?.fields.map((field, idx) => (
              <Box key={idx}>{renderField(field)}</Box>
            ))}
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            p: 3,
            justifyContent: "space-between",
            bgcolor: alpha(theme.palette.background.default, 0.5),
          }}
        >
          <Button
            startIcon={<EditIcon />}
            disabled={isVoyageReadOnly}
            onClick={handleToggleEditMode}
            variant={isEditMode ? "contained" : "outlined"}
            size="small"
            sx={{ fontWeight: 800 }}
          >
            {isEditMode ? "LOCK FIELDS" : "UNLOCK TO EDIT"}
          </Button>
          <Stack direction="row" spacing={1.5}>
            <Button
              onClick={() => setModalOpen(false)}
              sx={{ fontWeight: 700, color: "text.secondary" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              disabled={isVoyageReadOnly}
              sx={{ px: 4, fontWeight: 800, borderRadius: 2 }}
            >
              Print
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Protests;
